/**
 * What the signed-in admin may see: which permissions they hold, and whose
 * users they may list.
 */

/**
 * Whether the signed-in admin holds a permission.
 *
 * Permissions are stored at login as a JSON array of names in localStorage;
 * the names themselves are defined in the frontend (`allPermissions` in
 * pages/AdminUsers/AdminUserDashboard.js). Superusers hold every permission.
 *
 * The match is exact, the same way ProtectedRoute in routers/MainRouter.js
 * checks a route. A substring test on the stored string is not: "IPO" matches
 * "Create IPO Orders", which shows a menu item whose route then refuses entry.
 */
export const hasAdminPermission = (
  authUser: { isSuperuser?: boolean } | null | undefined,
  permission: string
): boolean => {
  if (authUser && authUser.isSuperuser) return true;
  try {
    const stored = JSON.parse(localStorage.getItem("permissions") || "[]");
    return Array.isArray(stored) && stored.indexOf(permission) >= 0;
  } catch (error) {
    return false;
  }
};

interface LoginUser {
  name?: string | null;
  isSuperuser?: boolean;
  can_see_all?: boolean;
}

/** The admin's `superuser_details`, stored at login as `loginUser`. */
const storedLoginUser = (): LoginUser | null => {
  try {
    return JSON.parse(localStorage.getItem("loginUser") || "null");
  } catch (error) {
    return null;
  }
};

/**
 * Whether the signed-in admin sees every user, rather than only their own
 * leads. Same rule as the Users, RFQ Orders and IPO Orders lists: superusers
 * and admins with `can_see_all` see everyone.
 */
export const canSeeAllLeads = (): boolean => {
  const loginUser = storedLoginUser();
  return Boolean(loginUser && (loginUser.isSuperuser || loginUser.can_see_all));
};

/**
 * Whether a user whose lead owner is `leadOwner` may be listed for the
 * signed-in admin: everyone for `canSeeAllLeads`, otherwise only the users the
 * admin owns (lead owner equal to their name, as on the Users page). An admin
 * with no name on record owns no one — an unassigned user must not match a
 * missing name.
 */
export const isVisibleLead = (leadOwner: string | null | undefined): boolean => {
  if (canSeeAllLeads()) return true;
  const loginUser = storedLoginUser();
  const ownName = String((loginUser && loginUser.name) || "").trim();
  return Boolean(ownName) && String(leadOwner || "").trim() === ownName;
};
